import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../styles/theme';
import { Button, Container, Section } from '../styles/components';
import CartItem from '../components/cart/CartItem';
import CartSummary from '../components/cart/CartSummary';
import { useCart } from '../context/CartContext';
import PageHero from '../components/layout/PageHero';

const PageContainer = styled.div`
  padding-bottom: ${theme.spacing.xl};
`;

const CartContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.lg};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0; // The CartSummary component already has its own margin/styling for mobile
  }
`;

const CartItems = styled.div`
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing.lg};
  box-shadow: ${theme.shadows.small};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
  
  svg {
    font-size: 4rem;
    color: ${theme.colors.gray};
    margin-bottom: ${theme.spacing.lg};
  }
  
  h2 {
    margin-bottom: ${theme.spacing.md};
  }
  
  p {
    margin-bottom: ${theme.spacing.lg};
    color: ${theme.colors.darkGray};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.lg} 0;
    
    svg {
      font-size: 3rem;
      margin-bottom: ${theme.spacing.md};
    }
    
    h2 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.sm};
  border-bottom: 1px solid ${theme.colors.lightGray};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.spacing.sm};
  }
`;

const ItemCount = styled.span`
  color: ${theme.colors.darkGray};
`;

const ContinueShopping = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  color: ${theme.colors.darkGray};
  text-decoration: none;
  
  &:hover {
    color: ${theme.colors.primary};
  }
`;

const CartActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.lg};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.lightGray};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.md};
  }
`;

const ClearButton = styled(Button)`
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
  }
`;

const CartPage: React.FC = () => {
  const { items, totalItems, clearCart } = useCart();
  
  if (items.length === 0) {
    return (
      <PageContainer>
        <PageHero 
          title="Your Shopping Cart" 
          subtitle="Review your items before checkout"
          height="30vh"
        />
        <Section>
          <Container>
            <EmptyCart>
              <FontAwesomeIcon icon={faShoppingCart} />
              <h2>Your Cart is Empty</h2>
              <p>Looks like you haven't added any items to your cart yet.</p>
              <Button as={Link} to="/products" variant="primary" size="large">
                Explore Our Collection
              </Button>
            </EmptyCart>
          </Container>
        </Section>
      </PageContainer>
    );
  }
  
  return (
    <PageContainer>
      <PageHero 
        title="Your Shopping Cart" 
        subtitle="Review your items before checkout"
        height="30vh"
      />
      
      <Section>
        <Container>
          <CartContainer>
            <CartItems>
              <CartHeader>
                <ItemCount>
                  {totalItems} {totalItems === 1 ? 'Item' : 'Items'}
                </ItemCount>
                <ContinueShopping to="/products">
                  <FontAwesomeIcon icon={faArrowLeft} /> Continue Shopping
                </ContinueShopping>
              </CartHeader>
              
              {items.map(item => (
                <CartItem key={item.id} item={item} />
              ))}
              
              <CartActions>
                <ClearButton 
                  onClick={clearCart} 
                  variant="outline"
                >
                  Clear Cart
                </ClearButton>
              </CartActions>
            </CartItems>
            
            <CartSummary />
          </CartContainer>
        </Container>
      </Section>
    </PageContainer>
  );
};

export default CartPage;